import React from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  alpha,
  useTheme,
} from "@mui/material";
import { AccessTime, Speed } from "@mui/icons-material";

const AvgEtc = ({ avgRate, lastAvgRate, etcAvg, etcCurrent }) => {
  const theme = useTheme();

  const formatDate = (date) => {
    if (!date) return "—";

    const d = date.toDate ? date.toDate() : new Date(date);
    if (isNaN(d.getTime())) return "—";

    return d.toLocaleString("sv-SE", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const StatBox = ({ label, value, icon, color, subValue }) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flex: 1,
        textAlign: "center",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
        {icon}
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight="700"
          sx={{ textTransform: "uppercase" }}
        >
          {label}
        </Typography>
      </Box>
      <Typography variant="body1" fontWeight="900" color={color}>
        {value}
      </Typography>
      {subValue && (
        <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 600 }}>
          {subValue}
        </Typography>
      )}
    </Box>
  );

  return (
    <Paper
      elevation={4}
      sx={{
        position: "sticky",
        top: 70, // Justera efter din header-höjd
        zIndex: 100,
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        mb: 2,
        background: alpha(theme.palette.background.paper, 0.95),
        backdropFilter: "blur(8px)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 1.5,
        }}
      >
        {/* Genomsnittlig ETC & Rate */}
        <StatBox
          label="ETC (Average)"
          value={formatDate(etcAvg)}
          icon={<AccessTime sx={{ fontSize: 14, color: "primary.main" }} />}
          color="primary.main"
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        <StatBox
          label="Avg Rate"
          value={`${avgRate || 0} m³`}
          icon={<Speed sx={{ fontSize: 14, color: "text.secondary" }} />}
        />

        <Box
          sx={{
            mx: 2,
            height: 30,
            width: "2px",
            bgcolor: alpha(theme.palette.divider, 0.5),
            borderRadius: 1,
          }}
        />

        {/* Nuvarande ETC & Rate */}
        <StatBox
          label="ETC (Current)"
          value={formatDate(etcCurrent)}
          icon={<AccessTime sx={{ fontSize: 14, color: "secondary.main" }} />}
          color="secondary.main"
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        <StatBox
          label="Last Rate"
          value={`${lastAvgRate || 0} m³`}
          icon={<Speed sx={{ fontSize: 14, color: "text.secondary" }} />}
        />
      </Box>

      {/* Progress bar visualisering (Valfritt men snyggt) */}
      <Box
        sx={{
          height: 4,
          width: "100%",
          bgcolor: alpha(theme.palette.primary.main, 0.1),
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: "100%",
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            opacity: 0.6,
          }}
        />
      </Box>
    </Paper>
  );
};

export default AvgEtc;
